import { queryOptions, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { sessionQuery } from '~/lib/session'
import { api, unwrap } from '~/lib/api'
import { toast } from '~/ui/toast'

// The member's own data. Keys start with 'self' (the server invalidates ['self'] after replies and
// absences, also from other devices) and carry team + season like every scoped key.
export const selfKeys = {
  all: ['self'] as const,
  scope: (teamId: string, season: string) => ['self', teamId, season] as const,
}

export const meQuery = (teamId: string, season: string) =>
  queryOptions({
    queryKey: [...selfKeys.scope(teamId, season), 'me'],
    queryFn: () => unwrap(api.me.$get()),
  })

export const mySessionsQuery = (teamId: string, season: string, from: string, to: string) =>
  queryOptions({
    queryKey: [...selfKeys.scope(teamId, season), 'sessions', from, to],
    queryFn: () => unwrap(api.me.sessions.$get({ query: { from, to } })),
  })
export type MySessions = Awaited<ReturnType<NonNullable<ReturnType<typeof mySessionsQuery>['queryFn']>>>
export type MySession = MySessions['items'][number]
export type MyMatch = MySessions['matches'][number]

export const myAbsencesQuery = (teamId: string, season: string) =>
  queryOptions({
    queryKey: [...selfKeys.scope(teamId, season), 'absences'],
    queryFn: () => unwrap(api.me.absences.$get()),
  })
export type MyAbsence = Awaited<ReturnType<NonNullable<ReturnType<typeof myAbsencesQuery>['queryFn']>>>['items'][number]

export const myStatsQuery = (teamId: string, season: string) =>
  queryOptions({
    queryKey: [...selfKeys.scope(teamId, season), 'stats'],
    queryFn: () => unwrap(api.me.stats.$get({ query: { season } })),
  })
export type MyStats = Awaited<ReturnType<NonNullable<ReturnType<typeof myStatsQuery>['queryFn']>>>

export const mySettlementsQuery = (teamId: string, season: string) =>
  queryOptions({
    queryKey: [...selfKeys.scope(teamId, season), 'settlements'],
    queryFn: () => unwrap(api.me.settlements.$get({ query: { season } })),
  })
export type MySettlements = Awaited<ReturnType<NonNullable<ReturnType<typeof mySettlementsQuery>['queryFn']>>>
export type MySettlement = MySettlements['items'][number]

export const myDocumentsQuery = (teamId: string, season: string) =>
  queryOptions({
    queryKey: [...selfKeys.scope(teamId, season), 'documents'],
    queryFn: () => unwrap(api.me.documents.$get()),
  })
export type MyDocument = Awaited<ReturnType<NonNullable<ReturnType<typeof myDocumentsQuery>['queryFn']>>>['items'][number]

export type Rsvp = 'yes' | 'no' | 'maybe'

/** Refetch the own screens and Heute after a write (SSE does the same for the coach's screens). */
function useRefresh() {
  const qc = useQueryClient()
  return () => Promise.all([qc.invalidateQueries({ queryKey: selfKeys.all }), qc.invalidateQueries({ queryKey: ['home'] })])
}

type ReplyCache = { items: { id: string; rsvp: Rsvp | null; rsvpNote: string | null }[] }

/** Reply to a session. Optimistic: every cached list shows the new answer at once, rolled back on error. */
export function useReply() {
  const qc = useQueryClient()
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (v: { sessionId: string; rsvp: Rsvp; note?: string | null }) =>
      unwrap(api.me.sessions[':id'].rsvp.$post({ param: { id: v.sessionId }, json: { rsvp: v.rsvp, note: v.note ?? null } })),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: selfKeys.all })
      const snapshot = qc.getQueriesData<ReplyCache>({ queryKey: selfKeys.all })
      for (const [key, data] of snapshot) {
        if (!data || !Array.isArray(data.items)) continue
        qc.setQueryData<ReplyCache>(key, {
          ...data,
          items: data.items.map((s) => (s.id === v.sessionId ? { ...s, rsvp: v.rsvp, rsvpNote: v.note ?? null } : s)),
        })
      }
      return { snapshot }
    },
    onError: (_e, _v, ctx) => ctx?.snapshot.forEach(([key, data]) => qc.setQueryData(key, data)),
    onSuccess: (_d, v) => toast.success(v.rsvp === 'yes' ? 'Zugesagt.' : v.rsvp === 'no' ? 'Abgesagt. Dein Trainer sieht den Grund.' : 'Als „Vielleicht“ gespeichert.'),
    onSettled: refresh,
  })
}

export function useMatchReply() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (v: { matchId: string; rsvp: Rsvp }) =>
      unwrap(api.me.matches[':id'].rsvp.$post({ param: { id: v.matchId }, json: { rsvp: v.rsvp } })),
    onSuccess: (_d, v) => toast.success(v.rsvp === 'yes' ? 'Nominierung bestätigt.' : v.rsvp === 'no' ? 'Absage gesendet.' : 'Gespeichert.'),
    onSettled: refresh,
  })
}

export type AbsenceInput = { kind: 'sick' | 'vacation' | 'personal'; from: string; until: string | null; reason: string | null }

export function useReportAbsence() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (v: AbsenceInput) => unwrap(api.me.absences.$post({ json: v })),
    // Field errors are shown in the dialog; only other failures need a toast.
    onError: () => undefined,
    onSuccess: () => toast.success('Gemeldet. Dein Trainerteam weiß Bescheid.'),
    onSettled: refresh,
  })
}

export function useEndAbsence() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (id: string) => unwrap(api.me.absences[':id'].$delete({ param: { id } })),
    onSuccess: (r) => toast.success(r.action === 'end' ? 'Willkommen zurück. Du bist wieder verfügbar.' : 'Eintrag gelöscht.'),
    onSettled: refresh,
  })
}

/** Route guard of /ich: only members with an own person and self-service; everyone else goes to Heute. */
export async function selfAllowed(qc: QueryClient) {
  const session = await qc.ensureQueryData(sessionQuery)
  const a = session.active
  return !!a?.personId && a.permissions.includes('self.service')
}
